import { useMutation, useSuspenseQuery, type UseMutationOptions } from "@tanstack/react-query";
import {
    getSurveyOptions,
    QuestionsForManagerQueryOptions,
    getSurveysOptions,
} from "@pulse/api/queryApi";
import { useContextError } from "common-frontend-components";
import { surveyContext } from "@pulse/contexts/surveyContext";
import {
    createQuestion,
    createSurvey,
    deleteQuestion,
    updateQuestion,
    type CreateQuestion,
    type Question,
} from "@pulse/api/surveyApi";
import { queryClient } from "@pulse/queryClient";

export const useCurrentSurvey = () =>
    useSuspenseQuery(
        getSurveyOptions({ surveyId: useContextError(surveyContext.name, surveyContext).id }),
    );

export const useCurrentQuestions = () => {
    const {
        data: { id },
    } = useCurrentSurvey();
    return useSuspenseQuery(QuestionsForManagerQueryOptions({ surveyId: id }));
};

export function useCreateSurvey({ pulseInstance }: { pulseInstance: number }) {
    return useMutation({
        mutationFn: async (data: { title: string; description: string | null }) =>
            await createSurvey({ pulseInstance, data }),
        onSuccess: async (createdSurvey) => {
            queryClient.setQueryData(
                getSurveyOptions({ surveyId: createdSurvey.id }).queryKey,
                createdSurvey,
            );
            // Invalidate the surveys list cache to refresh the list
            await queryClient.refetchQueries(
                getSurveysOptions({ pulseInstanceId: createdSurvey.pulseInstanceId }),
            );
        },
    });
}

export function useCreateQuestion(
    surveyId: number,
    options?: UseMutationOptions<Question, Error, CreateQuestion, unknown>,
) {
    return useMutation({
        ...options,
        mutationFn: (data: CreateQuestion) => createQuestion({ surveyId, data }),
        onSettled: () =>
            queryClient.invalidateQueries(QuestionsForManagerQueryOptions({ surveyId })),
    });
}

export function useRemoveQuestion(questionId: number) {
    return useMutation({
        mutationFn: () => deleteQuestion({ questionId }),
        onSuccess: (data) =>
            queryClient.invalidateQueries(
                QuestionsForManagerQueryOptions({ surveyId: data.surveyId }),
            ),
    });
}

export function useUpdateQuestion(questionId: number) {
    return useMutation({
        mutationFn: (data: Partial<CreateQuestion>) => updateQuestion({ questionId, data }),
        onSuccess: (data) =>
            queryClient.invalidateQueries(
                QuestionsForManagerQueryOptions({ surveyId: data.surveyId }),
            ),
    });
}
